import React, { useEffect, useState, useCallback } from "react";
import { supabase } from "../../lib/supabase";
import { AnimatePresence, motion } from "framer-motion";

/**
 * OffersTicker
 * Displays rotating active promotional offers on the homepage.
 * Fetches offers from Supabase and auto-rotates them with animation.
 */
type Offer = {
  id: number;
  message: string;
};

const ROTATE_MS = 4000;

const OffersTicker: React.FC = () => {
  const [offers, setOffers] = useState<Offer[]>([]);
  const [idx, setIdx] = useState(0);

  const fetchActiveOffers = useCallback(async () => {
    const { data, error } = await supabase
      .from("offers")
      .select("id, message")
      .order("created_at", { ascending: false });

    if (!error && data) {
      setOffers(data);
      setIdx((i) => (data.length ? i % data.length : 0));
    }
  }, []);

  useEffect(() => {
    fetchActiveOffers();
  }, [fetchActiveOffers]);

  useEffect(() => {
    if (offers.length <= 1) return;

    const id = setInterval(
      () => setIdx((i) => (i + 1) % offers.length),
      ROTATE_MS
    );

    return () => clearInterval(id);
  }, [offers.length]);

  if (offers.length === 0) return null;

  return (
    <section className="-mt-px">
      <div
        className="
          relative w-full h-[1.5in]
          flex items-center justify-center
          overflow-hidden
          bg-gradient-to-r from-[#ffeaea]/60 via-[#ffdede]/60 to-[#ffeaea]/60
          backdrop-blur-md
          shadow-sm
        "
      >
        <div className="absolute inset-0 bg-white/10 pointer-events-none" />

        <div className="relative z-10 text-center px-6">
          <h2 className="text-4xl font-script italic text-[#5c2f2f]">
            Special Offer
          </h2>

          <AnimatePresence mode="wait">
            <motion.div
              key={offers[idx]?.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.35 }}
              className="flex flex-col items-center gap-3"
            >
              <p className="text-2xl font-bold tracking-wide uppercase text-[#5c2f2f]">
                {offers[idx]?.message}
              </p>
            </motion.div>
          </AnimatePresence>
        </div>

        {offers.length > 1 && (
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 z-10 flex gap-2">
            {offers.map((_, i) => (
              <span
                key={i}
                className={`w-3 h-3 rounded-full transition-all duration-300 ${
                  i === idx ? "bg-[#a25d5d] scale-110" : "bg-[#f2cccc]"
                }`}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default OffersTicker;
